import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "motion/react";
import {
  ArrowLeft,
  User as UserIcon,
  Mail,
  Building2,
  Lock,
  Save,
  Check,
  Eye,
  EyeOff,
} from "lucide-react";
import { authApi } from "@/lib/auth";
import { useCompany } from "@/lib/CompanyContext";

const inputStyle: React.CSSProperties = {
  background: "var(--wf-paper)",
  border: "1px solid var(--wf-ink-5)",
  borderRadius: 8,
  padding: "9px 12px",
  fontSize: 14,
  fontFamily: "var(--wf-font-body)",
  color: "var(--wf-ink)",
  width: "100%",
  outline: "none",
};

const inputDisabledStyle: React.CSSProperties = {
  ...inputStyle,
  background: "#fafaf6",
  color: "var(--wf-ink-3)",
  cursor: "not-allowed",
};

function Section({
  icon,
  title,
  description,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section
      style={{
        background: "var(--wf-paper)",
        border: "1px solid var(--wf-ink-5)",
        borderRadius: 12,
        padding: 22,
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
      <header style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 9,
            background: "rgba(255,112,0,0.10)",
            color: "var(--nd-orange)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {icon}
        </div>
        <div>
          <h2
            className="wf-display"
            style={{
              fontSize: 16,
              fontWeight: 700,
              letterSpacing: "-0.015em",
              margin: 0,
            }}
          >
            {title}
          </h2>
          {description && (
            <p
              style={{
                fontSize: 12,
                color: "var(--wf-ink-3)",
                margin: 0,
                marginTop: 2,
              }}
            >
              {description}
            </p>
          )}
        </div>
      </header>
      {children}
    </section>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: "var(--wf-ink)" }}>
        {label}
      </span>
      {hint && (
        <span style={{ fontSize: 11, color: "var(--wf-ink-3)", marginTop: -2 }}>
          {hint}
        </span>
      )}
      {children}
    </label>
  );
}

function PasswordInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <input
        type={show ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete="off"
        style={{ ...inputStyle, paddingRight: 38 }}
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        aria-label={show ? "Esconder" : "Mostrar"}
        style={{
          position: "absolute",
          top: "50%",
          right: 8,
          transform: "translateY(-50%)",
          background: "transparent",
          border: "none",
          padding: 4,
          cursor: "pointer",
          color: "var(--wf-ink-3)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {show ? <EyeOff size={14} strokeWidth={1.75} /> : <Eye size={14} strokeWidth={1.75} />}
      </button>
    </div>
  );
}

export default function Perfil() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { company } = useCompany();

  const sessionQuery = useQuery({
    queryKey: ["auth", "session"],
    queryFn: () => authApi.getSession(),
  });
  const user = sessionQuery.data?.user;

  // Nome editável
  const [name, setName] = useState("");
  useEffect(() => {
    if (user?.name) setName(user.name);
  }, [user?.name]);
  const nameDirty = user?.name !== name && name.trim().length > 0;

  const saveName = useMutation({
    mutationFn: () => authApi.updateUser({ name: name.trim() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["auth", "session"] });
    },
  });

  // Trocar senha
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const passwordMatch =
    newPassword.length > 0 && newPassword === confirmPassword;
  const passwordValid =
    currentPassword.length >= 1 &&
    newPassword.length >= 8 &&
    passwordMatch;

  const changePassword = useMutation({
    mutationFn: () =>
      authApi.changePassword({ currentPassword, newPassword }),
    onSuccess: () => {
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    },
  });

  const initials = (user?.name ?? user?.email ?? "?")
    .split(/[\s@]/)
    .filter(Boolean)
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div>
      {/* Hero */}
      <div
        style={{
          background:
            "linear-gradient(120deg, #050810 0%, #0d0f1f 50%, #1a1424 100%)",
          color: "#fff",
          padding: "32px 32px 28px",
          borderBottom: "1px solid var(--wf-ink-5)",
        }}
      >
        <div style={{ maxWidth: 760, margin: "0 auto" }}>
          <button
            type="button"
            onClick={() => navigate(-1)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              fontSize: 12,
              padding: "4px 12px 4px 8px",
              border: "1px solid rgba(255,255,255,0.15)",
              borderRadius: 999,
              background: "rgba(255,255,255,0.03)",
              color: "rgba(255,255,255,0.75)",
              cursor: "pointer",
              fontFamily: "var(--wf-font-body)",
              marginBottom: 20,
              fontWeight: 500,
            }}
          >
            <ArrowLeft size={12} strokeWidth={2} /> Voltar
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                background: "var(--nd-orange)",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: "var(--wf-font-display)",
                fontSize: 22,
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {initials}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "rgba(255,255,255,0.55)",
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  marginBottom: 2,
                }}
              >
                Sua conta
              </div>
              <h1
                className="wf-display"
                style={{
                  fontSize: 24,
                  fontWeight: 700,
                  letterSpacing: "-0.02em",
                  lineHeight: 1.1,
                  margin: 0,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {user?.name ?? "—"}
              </h1>
              <div
                style={{
                  fontSize: 12,
                  color: "rgba(255,255,255,0.65)",
                  marginTop: 2,
                }}
              >
                {user?.email ?? ""}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: "28px 32px 48px" }}>
        <div
          style={{
            maxWidth: 760,
            margin: "0 auto",
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          {/* Nome + email */}
          <Section
            icon={<UserIcon size={15} strokeWidth={1.75} />}
            title="Identidade"
            description="Como sua equipe te chama"
          >
            <Field label="Nome">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Como você quer ser chamado"
                style={inputStyle}
              />
            </Field>
            <Field label="E-mail" hint="Não pode ser alterado por aqui">
              <div style={{ position: "relative" }}>
                <Mail
                  size={14}
                  strokeWidth={1.75}
                  color="var(--wf-ink-3)"
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: 11,
                    transform: "translateY(-50%)",
                  }}
                />
                <input
                  value={user?.email ?? ""}
                  disabled
                  style={{ ...inputDisabledStyle, paddingLeft: 34 }}
                />
              </div>
            </Field>

            {(nameDirty || saveName.isSuccess) && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginTop: 4,
                }}
              >
                {saveName.isSuccess && !nameDirty && (
                  <motion.span
                    initial={{ opacity: 0, x: -4 }}
                    animate={{ opacity: 1, x: 0 }}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                      fontSize: 12,
                      color: "#2a7a2a",
                      fontWeight: 500,
                    }}
                  >
                    <Check size={12} strokeWidth={2} /> salvo
                  </motion.span>
                )}
                <div style={{ flex: 1 }} />
                {nameDirty && (
                  <button
                    type="button"
                    onClick={() => saveName.mutate()}
                    disabled={saveName.isPending}
                    className="wf-btn wf-btn--primary wf-btn--sm"
                    style={{ display: "inline-flex", alignItems: "center", gap: 5 }}
                  >
                    {saveName.isPending ? "salvando…" : "Salvar nome"}
                    <Save size={11} strokeWidth={2} color="#fff" />
                  </button>
                )}
              </div>
            )}

            {saveName.isError && (
              <div
                style={{
                  fontSize: 12,
                  color: "var(--nd-orange-pun)",
                  background: "#fff1e6",
                  border: "1px solid var(--nd-orange)",
                  borderRadius: 8,
                  padding: "8px 12px",
                }}
              >
                {(saveName.error as Error)?.message ?? "erro ao salvar"}
              </div>
            )}
          </Section>

          {/* Empresa */}
          <Section
            icon={<Building2 size={15} strokeWidth={1.75} />}
            title="Empresa"
            description="Workspace ativo"
          >
            <div
              style={{
                padding: "10px 12px",
                background: "#fafaf6",
                border: "1px solid var(--wf-ink-5)",
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 500,
                color: "var(--wf-ink)",
              }}
            >
              {company?.name ?? "—"}
            </div>
          </Section>

          {/* Trocar senha */}
          <Section
            icon={<Lock size={15} strokeWidth={1.75} />}
            title="Trocar senha"
            description="Use uma senha forte com pelo menos 8 caracteres"
          >
            <Field label="Senha atual">
              <PasswordInput
                value={currentPassword}
                onChange={setCurrentPassword}
                placeholder="Digite sua senha atual"
              />
            </Field>
            <Field label="Nova senha" hint="Mínimo 8 caracteres">
              <PasswordInput
                value={newPassword}
                onChange={setNewPassword}
                placeholder="Sua nova senha"
              />
            </Field>
            <Field label="Confirmar nova senha">
              <PasswordInput
                value={confirmPassword}
                onChange={setConfirmPassword}
                placeholder="Repita a nova senha"
              />
            </Field>
            {confirmPassword.length > 0 && !passwordMatch && (
              <div style={{ fontSize: 11, color: "#cc3344" }}>
                As senhas não coincidem
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 4 }}>
              <button
                type="button"
                onClick={() => changePassword.mutate()}
                disabled={!passwordValid || changePassword.isPending}
                className="wf-btn wf-btn--primary wf-btn--sm"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  opacity: !passwordValid || changePassword.isPending ? 0.5 : 1,
                  cursor:
                    !passwordValid || changePassword.isPending
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                {changePassword.isPending ? "trocando…" : "Trocar senha"}
                <Save size={11} strokeWidth={2} color="#fff" />
              </button>
            </div>

            {changePassword.isSuccess && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "6px 10px",
                  background: "rgba(92,184,92,0.10)",
                  border: "1px solid #5cb85c",
                  borderRadius: 8,
                  color: "#2a7a2a",
                  fontSize: 12,
                  fontWeight: 500,
                  width: "fit-content",
                }}
              >
                <Check size={12} strokeWidth={2} /> Senha trocada com sucesso
              </motion.div>
            )}

            {changePassword.isError && (
              <div
                style={{
                  fontSize: 12,
                  color: "var(--nd-orange-pun)",
                  background: "#fff1e6",
                  border: "1px solid var(--nd-orange)",
                  borderRadius: 8,
                  padding: "8px 12px",
                }}
              >
                {(changePassword.error as Error)?.message ?? "erro ao trocar senha"}
              </div>
            )}
          </Section>
        </div>
      </div>
    </div>
  );
}
